import { useMemo, useRef } from 'react';
import { useParams } from 'react-router';

import theme from '@/constants/theme';
import useLearningRoutes from '@/context/routes/hook';
import { EmojiEvents } from '@mui/icons-material';
import HeaderNavigation from '@/components/navigation/HeaderNavigation';
import { routes } from '@/constants/routes';
import useUser from '@/hooks/useUser';
import RouteCover from '@/components/routes/RouteDetail/RouteCover';
import InitiatedPath from '@/components/routes/RouteDetail/InitiatedRoute';

import useLoadLearningContents from './useLoadLearningContents';
import useLearningStages from './useLoadLearningStages';

const RouteScreen = () => {
  const { routeId } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const { loading: loadingContents, learningPathContents } = useLoadLearningContents({ routeId });
  const { loading: loadingStages, routeStages } = useLearningStages({ routeId });

  const { user } = useUser();
  const { getRouteById, loadingRoutes } = useLearningRoutes();

  const currentRoute = useMemo(() => getRouteById(routeId as string), [getRouteById, routeId]);

  const handleResetScroll = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  };

  const loading = loadingContents || loadingStages || loadingRoutes;

  return (
    <div
      ref={containerRef}
      style={{
        backgroundColor: theme.colors.background.primary,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        position: 'relative',
        maxHeight: '100%',
        overflow: 'auto',
      }}
    >
      <HeaderNavigation
        loading={loading}
        title={currentRoute?.title}
        backHref={routes.routes.root}
        rightContent={
          <p
            style={{
              color: theme.colors.points,
              fontSize: 16,
              display: 'flex',
              gap: 4,
              alignItems: 'center',
            }}
          >
            {user?.points ?? 0}
            <EmojiEvents />
          </p>
        }
      />
      {(currentRoute?.progressStatus === 'NOT_STARTED' || !currentRoute) && (
        <RouteCover
          routeId={currentRoute?.id}
          title={currentRoute?.title}
          description={currentRoute?.description}
          coverImage={currentRoute?.coverImage}
          contents={learningPathContents?.contents}
          onStart={handleResetScroll}
          stages={routeStages}
          loading={loading}
        />
      )}
      {(currentRoute?.progressStatus === 'IN_PROGRESS' || currentRoute?.progressStatus === 'COMPLETED') && (
        <InitiatedPath
          routeId={currentRoute?.id}
          userRouteProgressId={currentRoute?.userRouteProgressId}
          progress={currentRoute?.progress}
          contents={learningPathContents?.contents}
          stages={routeStages}
          stagesProgress={currentRoute?.stagesProgress}
          loading={loading}
        />
      )}
    </div>
  );
};

export default RouteScreen;
